import React, { useState, useEffect } from "react";
import { Typography, TextField, Button, FormControl, MenuItem, Collapse } from "@mui/material";
import { Box, Container } from "@mui/system";
import Select from '@mui/material/Select';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import NCRFormLabel from './NCRFormLabel';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import NCRFormHeading from './NCRFormHeading';
import DateRangePicker from "./DateRangePicker";
import { useSelector } from "react-redux";

export default function SearchCriteria({ onChange, initialDateRange, initialFilters = {} }) {
  const [open, setOpen] = useState(false);
  const lookupData = useSelector((state) => state.lookup.data) || {};

  const type = lookupData["NCR Type"] || [];
  const departments = lookupData["Department"] || [];
  const categories = lookupData["Non-conformance Category"] || [];
  const statuses = lookupData["Status"] || [];
  const responsibleTeams = lookupData["Responsible Team/Person"] || [];
  const correctiveActionRequired = lookupData["Corrective action required"] || [];
  const locationLevel = lookupData["Location"] || [];

  const initiateFilterValue = {
    ncr_id: '',
    type: '',
    date_range: '',
    department: '',
    category: '',
    status: '',
    responsible_team_or_person: '',
    affected_item_component_no: '',
    affected_item_supplier_cust_name: '',
    location: '',
    corrective_action_required: '',
    keywords: '',
  }

  const [filterValue, setFilterValue] = useState(initiateFilterValue);

  useEffect(() => {
    // Set initial date range if provided
    if (initialDateRange) {
      setFilterValue((prev) => ({
        ...prev,
        date_range: initialDateRange,
      }));
    }
  }, [initialDateRange]);

  useEffect(() => {
    // Set additional initial filters if provided
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setFilterValue((prev) => ({
        ...prev,
        ...initialFilters,
      }));
    }
  }, [initialFilters]);  
  
  const handleSearchResultsClick = (e) => {
    e.preventDefault();
    const filterValueToSearch = Object.fromEntries(
      Object.entries(filterValue).filter(([_, value]) => {
        if (typeof value === 'string') {
          return value.trim() !== '';
        }
        return value !== '' && value !== null && value !== undefined;
      })
    );
    onChange(filterValueToSearch);
  }

  const theme = createTheme({
    components: {
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: "14px",
            fontWeight: 400,
            color: "rgba(84, 84, 84, 1)",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&::placeholder": {
              fontSize: "14px",
              fontWeight: 400,
              color: "rgba(84, 84, 84, 1)",
              opacity: 1,
            },
          },
        },
      },
    },
  });

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleChange = (event) => {
    if (event?.target) {
      const { name, value } = event.target;
      setFilterValue((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (event?.name === "dateRange") {
      const { start, end } = event.value;
      setFilterValue((prev) => ({
        ...prev,
        date_range: start && end ? `${start}.${end}` : "",
      }));
    }
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Container maxWidth={false} sx={{ border: '1px solid rgba(224, 224, 224, 1)', borderRadius: '10px' }}>
          <form onSubmit={handleSearchResultsClick}>
            <div>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ mt: 2 }}>
                  <NCRFormHeading
                    tooltipData={
                      [
                        { description: "Find relevant records by entering search criteria in the search menu to filter and locate specific content efficiently." },
                      ]
                    }
                  >
                    Search criteria:
                  </NCRFormHeading>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleToggle}>
                  <Typography sx={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#2A3DBD',
                    pr: 1
                  }}>
                    Show advanced filters
                  </Typography>
                  {true ? open ? <ExpandLess sx={{ color: '#2A3DBD', fontSize: '28px', fontWeight: 100 }} /> : <ExpandMore sx={{ color: '#2A3DBD', fontSize: '28px' }} /> : ''}
                </Box>
              </Box>
              <div style={{ display: 'flex' }}>
                <div style={{ width: '35%', paddingRight: '80px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <NCRFormLabel label="search-label">
                      NC ID:
                    </NCRFormLabel>
                    <TextField
                      size='small'
                      name="ncr_id"
                      sx={{
                        width: '200px',
                      }}
                      onChange={handleChange}
                      value={filterValue.ncr_id}
                      placeholder="Enter NC ID"
                    />
                  </div>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        Department/<br /> Function:
                      </NCRFormLabel>
                      <FormControl variant="outlined">
                        <Select
                          id="department"
                          sx={{
                            width: '200px',
                          }}
                          size='small'
                          name="department"
                          onChange={handleChange}
                          value={filterValue.department}
                          displayEmpty
                          renderValue={(selected) => (selected ? selected : "Select option")}
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          MenuProps={{
                            disablePortal: true,
                          }}
                        >
                          {departments.map((type, index) => (
                            <MenuItem key={index} value={type} className='dropdown-menu-item'>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Collapse>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        Responsible Person/<br /> Team:
                      </NCRFormLabel>
                      <FormControl variant="outlined">
                        <Select
                          sx={{ width: '200px', height: '42px' }}
                          name="responsible_team_or_person"
                          onChange={handleChange}
                          value={filterValue.responsible_team_or_person}
                          displayEmpty
                          renderValue={(selected) => (selected ? selected : "Select option")}
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          MenuProps={{
                            disablePortal: true,
                          }}
                        >
                          {responsibleTeams.map((type, index) => (
                            <MenuItem key={index} value={type} className='dropdown-menu-item'>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Collapse>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        NC Location:
                      </NCRFormLabel>
                      <FormControl variant="outlined">
                        <Select
                          sx={{ width: '200px', height: '42px' }}
                          name="location"
                          onChange={handleChange}
                          value={filterValue.location}
                          displayEmpty
                          renderValue={(selected) => (selected ? selected : "Select option")}
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          MenuProps={{
                            disablePortal: true,
                          }}
                        >
                          {locationLevel.map((type, index) => (
                            <MenuItem key={index} value={type} className='dropdown-menu-item'>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Collapse>
                </div>
                <div style={{ width: '35%', paddingRight: '80px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <NCRFormLabel label="search-label">
                      NC type:
                    </NCRFormLabel>
                    <FormControl variant="outlined">
                      <Select
                        name="type"
                        sx={{
                          width: '200px', height: '42px',
                        }}
                        value={filterValue.type}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(selected) => (selected ? selected : "Select option")}
                        inputProps={{
                          "aria-label": "Without label",
                        }}
                        MenuProps={{
                          disablePortal: true,
                        }}
                      >
                        {type.map((type, index) => (
                          <MenuItem key={index} value={type} className='dropdown-menu-item'>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        NC category:
                      </NCRFormLabel>
                      <FormControl variant="outlined">
                        <Select
                          id="category"
                          sx={{
                            width: '200px', height: '42px',
                          }}
                          name="category"
                          onChange={handleChange}
                          value={filterValue.category}
                          displayEmpty
                          renderValue={(selected) => (selected ? selected : "Select option")}
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          MenuProps={{
                            disablePortal: true,
                          }}
                        >
                          {categories.map((type, index) => (
                            <MenuItem key={index} value={type} className='dropdown-menu-item'>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Collapse>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        Affected Item/<br /> Component:
                      </NCRFormLabel>
                      <TextField
                        size='small'
                        sx={{
                          width: '200px',
                        }}
                        name="affected_item_component_no"
                        onChange={handleChange}
                        value={filterValue.affected_item_component_no}
                        placeholder="Enter affected item"
                      />
                    </div>
                  </Collapse>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        Corrective<br /> action required:
                      </NCRFormLabel>
                      <FormControl variant="outlined">
                        <Select
                          sx={{ width: '200px', height: '42px' }}
                          name="corrective_action_required"
                          onChange={handleChange}
                          value={filterValue.corrective_action_required}
                          displayEmpty
                          renderValue={(selected) => (selected ? selected : "Select option")}
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          MenuProps={{
                            disablePortal: true,
                          }}
                        >
                          {correctiveActionRequired.map((type, index) => (
                            <MenuItem key={index} value={type} className='dropdown-menu-item'>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Collapse>
                </div>
                <div style={{ width: '30%', }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <NCRFormLabel label="search-label">
                      Date range:
                    </NCRFormLabel>
                    <DateRangePicker
                      name="dateRange"
                      value={filterValue?.date_range || null}
                      onChange={(value) => handleChange({ name: "dateRange", value })}
                    />
                  </div>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        Status:
                      </NCRFormLabel>
                      <FormControl variant="outlined">
                        <Select
                          id="status"
                          sx={{ width: '200px', height: '42px' }}
                          name="status"
                          onChange={handleChange}
                          value={filterValue.status}
                          displayEmpty
                          renderValue={(selected) => (selected ? selected : "Select option")}
                          inputProps={{
                            "aria-label": "Without label",
                          }}
                          MenuProps={{
                            disablePortal: true,
                          }}
                        >
                          {statuses.map((type, index) => (
                            <MenuItem key={index} value={type} className='dropdown-menu-item'>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </Collapse>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        Customer/ Supplier:
                      </NCRFormLabel>
                      <TextField
                        size='small'
                        sx={{
                          width: '200px',
                        }}
                        name="affected_item_supplier_cust_name"
                        onChange={handleChange}
                        value={filterValue.affected_item_supplier_cust_name}
                        placeholder="Enter customer/Supplier"
                      />
                    </div>
                  </Collapse>
                  <Collapse in={open}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <NCRFormLabel label="search-label">
                        Keywords:
                      </NCRFormLabel>
                      <TextField
                        size='small'
                        // disabled
                        sx={{
                          width: '200px',
                        }}
                        name="keywords"
                        onChange={handleChange}
                        value={filterValue.keywords}
                        placeholder="Enter keyword"
                      />
                    </div>
                  </Collapse>
                </div>
              </div>
            </div>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, pb: 2 }}>
              <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="neutral"
                  sx={{ fontFamily: 'Lato', fontWeight: 400, fontSize: '14px', border: '1px solid rgba(14, 41, 102, 1)', textTransform: 'none', borderRadius: '10px', color: 'rgba(14, 41, 102, 1)', py: 1 }}
                  onClick={() => setFilterValue(initiateFilterValue)}
                >
                  Reset
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="neutral"
                  type="submit"
                  sx={{ fontFamily: 'Lato', fontWeight: '700', fontSize: '14px', background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)', textTransform: 'none', color: 'rgba(255, 255, 255, 1)', borderRadius: '10px', py: 1, px: 3 }}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </form>
        </Container>
      </ThemeProvider>
    </>
  );
};

